import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  Code2,
  Download,
  ExternalLink,
  Github,
  Linkedin,
  Mail,
  MapPin,
  Monitor,
  Sparkles,
  UserRound,
} from 'lucide-react';
import { PROJECTS } from '@/data/projects-data';
import type { Project } from '@/data/types';

interface RecruiterPortfolioProps {
  onEnterDesktop: () => void;
}

const FEATURED_IDS = ['hmx', 'agrifertile', 'p-men-rag'];

function getProjectFromPath(): Project | null {
  const match = window.location.pathname.match(/^\/projects\/([^/]+)\/?$/);
  return match ? PROJECTS.find((project) => project.id === match[1]) ?? null : null;
}

function navigate(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function RecruiterPortfolio({ onEnterDesktop }: RecruiterPortfolioProps) {
  const [activeProject, setActiveProject] = useState<Project | null>(() => getProjectFromPath());
  const featuredProjects = useMemo(
    () => FEATURED_IDS.map((id) => PROJECTS.find((project) => project.id === id)).filter(Boolean) as Project[],
    [],
  );

  useEffect(() => {
    const syncPath = () => setActiveProject(getProjectFromPath());
    window.addEventListener('popstate', syncPath);
    return () => window.removeEventListener('popstate', syncPath);
  }, []);

  useEffect(() => {
    document.title = activeProject
      ? `${activeProject.title} | Lukman Afandi`
      : 'Lukman Afandi | Software Engineer building AI-powered enterprise products';
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeProject]);

  const openProject = (project: Project) => navigate(`/projects/${project.id}`);
  const closeProject = () => navigate('/');

  if (activeProject) {
    return <RecruiterProjectDetail project={activeProject} onBack={closeProject} onEnterDesktop={onEnterDesktop} />;
  }

  return (
    <div className="recruiter-shell min-h-screen text-slate-950">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <nav className="recruiter-launcher" aria-label="Quick portfolio links">
        <a href="#main-content"><UserRound aria-hidden="true" /><span>Profile</span></a>
        <a href="#work"><BriefcaseBusiness aria-hidden="true" /><span>Projects</span></a>
        <button type="button" onClick={onEnterDesktop}><Monitor aria-hidden="true" /><span>Desktop</span></button>
        <a href="#contact"><Mail aria-hidden="true" /><span>Contact</span></a>
      </nav>

      <header className="recruiter-header" aria-label="Primary navigation">
        <a href="#main-content" className="recruiter-brand" aria-label="Lukman Afandi home">
          <span className="recruiter-brand-mark" aria-hidden="true"><Code2 size={20} /></span>
          <span>
            <strong>Lukman Afandi</strong>
            <small>Software Engineer</small>
          </span>
        </a>
        <nav className="recruiter-nav" aria-label="Portfolio sections">
          <a href="#work">Work</a>
          <a href="#experience">Experience</a>
          <a href="#contact">Contact</a>
          <button type="button" className="desktop-experience-trigger" onClick={onEnterDesktop}>
            <Monitor size={16} aria-hidden="true" /> Desktop mode
          </button>
        </nav>
      </header>

      <main id="main-content" className="recruiter-main">
        <section className="recruiter-window" aria-labelledby="hero-title">
          <div className="recruiter-window-bar">
            <div className="recruiter-window-title"><Sparkles size={17} /> Recruiter Command Center</div>
            <div className="recruiter-window-dots" aria-hidden="true"><span /><span /><span /></div>
          </div>

          <div className="recruiter-hero">
            <div className="recruiter-hero-copy">
              <p className="availability"><span aria-hidden="true" /> Open to meaningful opportunities</p>
              <h1 id="hero-title">Lukman Afandi</h1>
              <p className="recruiter-kicker">Software Engineer building AI-powered enterprise products</p>
              <p className="recruiter-intro">
                I build secure, scalable systems that turn complex enterprise workflows into intelligent,
                actionable experiences—from AI-native ERP interfaces and RAG platforms to cloud-ready products.
              </p>
              <div className="recruiter-actions">
                <a className="primary" href="#work"><Sparkles size={18} /> View featured work</a>
                <a href="/Profile.pdf" download><Download size={18} /> Download résumé</a>
                <button type="button" className="desktop-experience-trigger" onClick={onEnterDesktop}><Monitor size={18} /> Explore interactive desktop</button>
              </div>
            </div>
            <div className="recruiter-portrait-wrap">
              <img src="/profile-photo.jpg" alt="Portrait of Lukman Afandi" className="recruiter-portrait" />
            </div>
          </div>

          <dl className="recruiter-facts" aria-label="Professional summary">
            <div><BriefcaseBusiness aria-hidden="true" /><span><dt>Role</dt><dd>Software Engineer</dd></span></div>
            <div><MapPin aria-hidden="true" /><span><dt>Location</dt><dd>Indonesia · UTC+7</dd></span></div>
            <div><Code2 aria-hidden="true" /><span><dt>Specialties</dt><dd>AI Systems · Enterprise Integration · Full-Stack</dd></span></div>
          </dl>

          <section id="work" className="recruiter-work" aria-labelledby="featured-work-title">
            <div className="recruiter-section-heading">
              <div>
                <p>Selected case studies</p>
                <h2 id="featured-work-title">Featured Work</h2>
              </div>
              <button type="button" className="desktop-experience-trigger" onClick={onEnterDesktop}>View all projects <ArrowRight size={16} /></button>
            </div>
            <div className="recruiter-project-grid">
              {featuredProjects.map((project) => (
                <article className="recruiter-project" key={project.id}>
                  <button type="button" onClick={() => openProject(project)} aria-label={`Read case study: ${project.title}`}>
                    <div className="recruiter-project-image-wrap">
                      <img src={project.image} alt={`${project.title} interface`} loading="lazy" />
                      <span>{project.status === 'in-progress' ? 'In progress' : 'Case study'}</span>
                      <time>{project.year}</time>
                    </div>
                    <div className="recruiter-project-copy">
                      <p className="recruiter-project-role">{project.role}</p>
                      <h3>{project.title}</h3>
                      <p>{project.description}</p>
                      <div className="recruiter-tags" aria-label="Technologies">
                        {project.tags.slice(0, 3).map((tag) => <span key={tag}>{tag}</span>)}
                      </div>
                      <strong>Read case study <ArrowRight size={15} /></strong>
                    </div>
                  </button>
                </article>
              ))}
            </div>
          </section>
        </section>

        <section id="experience" className="recruiter-experience" aria-labelledby="experience-title">
          <div>
            <p className="eyebrow">Current focus</p>
            <h2 id="experience-title">Enterprise engineering, from concept to production</h2>
          </div>
          <div className="recruiter-experience-copy">
            <p>
              I work across frontend architecture, backend services, AI integrations, CI/CD, and cloud delivery.
              My recent work centers on turning operational complexity into dependable product experiences.
            </p>
            <ul>
              <li><CheckCircle2 /> AI-native interfaces and RAG workflows</li>
              <li><CheckCircle2 /> Laravel, Vue, React, Python, and Google Cloud</li>
              <li><CheckCircle2 /> Enterprise product delivery and cross-functional collaboration</li>
            </ul>
          </div>
        </section>

        <section id="contact" className="recruiter-contact" aria-labelledby="contact-title">
          <div>
            <p className="eyebrow">Let’s work together</p>
            <h2 id="contact-title">Have a role or project where I can make an impact?</h2>
            <p>Send me the context and I’ll reply from my real inbox—usually within two business days.</p>
          </div>
          <div className="recruiter-contact-actions">
            <a className="primary" href="mailto:lukmanafandi10@gmail.com?subject=Portfolio%20inquiry"><Mail size={18} /> Email Lukman</a>
            <a href="https://linkedin.com/in/lukman-afandi" target="_blank" rel="noreferrer"><Linkedin size={18} /> LinkedIn</a>
            <a href="https://github.com/CodeVectorizer" target="_blank" rel="noreferrer"><Github size={18} /> GitHub</a>
          </div>
        </section>
      </main>

      <footer className="recruiter-footer">
        <span>© {new Date().getFullYear()} Lukman Afandi</span>
        <button type="button" className="desktop-experience-trigger" onClick={onEnterDesktop}><Monitor size={16} /> Open interactive desktop</button>
      </footer>
      <nav className="recruiter-dock" aria-label="Portfolio shortcuts">
        <a href="#main-content" aria-label="Profile"><UserRound /></a>
        <a href="#work" aria-label="Featured projects"><BriefcaseBusiness /></a>
        <button type="button" onClick={onEnterDesktop} aria-label="Interactive desktop"><Monitor /></button>
        <a href="mailto:lukmanafandi10@gmail.com?subject=Portfolio%20inquiry" aria-label="Email Lukman"><Mail /></a>
      </nav>
    </div>
  );
}

function RecruiterProjectDetail({ project, onBack, onEnterDesktop }: { project: Project; onBack: () => void; onEnterDesktop: () => void }) {
  return (
    <div className="recruiter-shell min-h-screen text-slate-950">
      <a href="#project-content" className="skip-link">Skip to project content</a>
      <header className="recruiter-header">
        <button type="button" className="recruiter-back" onClick={onBack}><ArrowLeft size={18} /> Portfolio overview</button>
        <button type="button" className="recruiter-desktop-button desktop-experience-trigger" onClick={onEnterDesktop}><Monitor size={17} /> Desktop mode</button>
      </header>
      <main id="project-content" className="case-study-main">
        <section className="case-study-hero">
          <div className="case-study-title">
            <p className="eyebrow">Featured case study · {project.year}</p>
            <h1>{project.title}</h1>
            <p>{project.longDescription ?? project.description}</p>
            <div className="recruiter-tags">{project.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
          </div>
          <img src={project.image} alt={`${project.title} product interface`} />
        </section>

        <dl className="case-study-facts">
          <div><UserRound /><dt>My role</dt><dd>{project.role ?? 'Software Engineer'}</dd></div>
          <div><BriefcaseBusiness /><dt>Organization</dt><dd>{project.organization ?? 'Independent'}</dd></div>
          <div><CalendarDays /><dt>Period</dt><dd>{project.period ?? project.year}</dd></div>
        </dl>

        <section className="case-study-story">
          <div>
            <p className="eyebrow">Contribution</p>
            <h2>What I delivered</h2>
            <p>
              My contribution focused on turning the product’s core requirements into reliable, usable workflows
              and connecting the experience to the underlying enterprise systems.
            </p>
          </div>
          <ul>
            {(project.highlights ?? []).map((highlight) => <li key={highlight}><CheckCircle2 /> {highlight}</li>)}
          </ul>
        </section>

        {project.evidenceImages?.length ? (
          <section className="case-study-evidence" aria-labelledby="evidence-title">
            <p className="eyebrow">Evidence</p>
            <h2 id="evidence-title">Product screens and workflow proof</h2>
            <div>
              {project.evidenceImages.map((image, index) => (
                <figure key={image}>
                  <img src={image} alt={`${project.title} evidence screen ${index + 1}`} loading="lazy" />
                  <figcaption>{project.title} · interface evidence {index + 1}</figcaption>
                </figure>
              ))}
            </div>
          </section>
        ) : null}

        <section className="case-study-cta">
          <div><p className="eyebrow">Next step</p><h2>Interested in how I approach product engineering?</h2></div>
          <div>
            {project.demo ? <a href={project.demo} target="_blank" rel="noreferrer"><ExternalLink size={18} /> Visit company site</a> : null}
            <a className="primary" href="mailto:lukmanafandi10@gmail.com?subject=Portfolio%20project%20inquiry"><Mail size={18} /> Start a conversation</a>
          </div>
        </section>
      </main>
    </div>
  );
}
